import { useEffect, useMemo, useState } from "react";
import { Clock, RefreshCw, Plus, Trash2, AlertTriangle, X, ChevronLeft, ChevronRight, Users , CalendarDays} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DAYS_NL = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];
const FUNCTION_OPTIONS = [
  { v: "chef", l: "Chef" },
  { v: "tandoori_kok", l: "Tandoori-kok" },
  { v: "oven_grill_kok", l: "Oven/Grill-kok" },
  { v: "kitchen_porter", l: "Kitchen porter" },
  { v: "hulp_stagiair", l: "Hulp / stagiair" },
  { v: "bediening", l: "Bediening" },
  { v: "beveiliging", l: "Beveiliging" },
];

function weekStart(d = new Date()) {
  const nd = new Date(d);
  const day = (nd.getDay() + 6) % 7; // Monday-based
  nd.setDate(nd.getDate() - day);
  nd.setHours(0, 0, 0, 0);
  return nd;
}
function iso(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const nd = new Date(d); nd.setDate(nd.getDate() + n); return nd; }
function isoWeekNr(d) {
  const nd = new Date(d); nd.setHours(0, 0, 0, 0);
  nd.setDate(nd.getDate() + 3 - ((nd.getDay() + 6) % 7));
  const first = new Date(nd.getFullYear(), 0, 4);
  return 1 + Math.round(((nd - first) / 86400000 - 3 + ((first.getDay() + 6) % 7)) / 7);
}

export default function ShiftRegistratie() {
  const { user } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [rid, setRid] = useState("");
  const [weekStartDate, setWeekStartDate] = useState(weekStart());
  const [shifts, setShifts] = useState([]);
  const [violations, setViolations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [editing, setEditing] = useState(null); // shift or null
  const [autoFilling, setAutoFilling] = useState(false);
  const [addingExternal, setAddingExternal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStartDate, i)), [weekStartDate]);
  const fromISO = iso(days[0]);
  const toISO = iso(days[6]);

  useEffect(() => {
    api.get("/restaurants").then(r => {
      setRestaurants(r.data || []);
      if (r.data?.length && !rid) setRid(r.data[0].id);
    }).catch(() => {});
  }, []);

  const loadWeek = () => {
    if (!rid) return;
    api.get(`/shifts?restaurant_id=${rid}&date_from=${fromISO}&date_to=${toISO}`)
      .then(r => { setShifts(r.data?.shifts || []); setViolations(r.data?.violations || []); })
      .catch(() => {});
    api.get(`/shifts/employee_settings?restaurant_id=${rid}`)
      .then(r => setEmployees(r.data || [])).catch(() => {});
  };
  useEffect(loadWeek, [rid, fromISO, toISO]);

  const cellShifts = (empId, dayISO) =>
    shifts.filter(s => s.employee_id === empId && s.date === dayISO);
  const violationsFor = (shiftId) => violations.filter(v => v.shift_id === shiftId);
  const dailyViolations = (empId, dayISO) => violations.filter(v => v.employee_id === empId && v.date === dayISO);

  const setFunction = async (emp, fn) => {
    setEmployees(es => es.map(e => e.employee_id === emp.employee_id ? { ...e, function: fn } : e));
    try {
      await api.put("/shifts/employee_settings", { restaurant_id: rid, employee_id: emp.employee_id, function: fn });
      toast.success(`${emp.name} · functie: ${FUNCTION_OPTIONS.find(o => o.v === fn)?.l || fn}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
      setEmployees(es => es.map(x => x.employee_id === emp.employee_id ? { ...x, function: emp.function } : x));
    }
  };

  const runAutoFill = async () => {
    if (!confirm("Rooster van de komende 2 weken invullen op basis van eerdere weken?")) return;
    setAutoFilling(true);
    try {
      const start = iso(addDays(weekStartDate, 7));
      const end = iso(addDays(weekStartDate, 20));
      const r = await api.post("/shifts/auto_fill", { restaurant_id: rid, from_date: start, to_date: end });
      toast.success(`${r.data.created} shifts toegevoegd aan het rooster`);
      loadWeek();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bijwerken mislukt");
    }
    setAutoFilling(false);
  };

  const setMaxHours = async (emp, val) => {
    const n = Math.max(4, Math.min(60, Number(val) || 0));
    setEmployees(es => es.map(e => e.employee_id === emp.employee_id ? { ...e, max_hours_week: n } : e));
    try {
      await api.put("/shifts/employee_settings", { restaurant_id: rid, employee_id: emp.employee_id, max_hours_week: n });
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
  };

  const generateRoster = async (force) => {
    if (force && !confirm("Automatisch gegenereerde shifts van deze week opnieuw maken? Handmatig ingevoerde dagen blijven staan.")) return;
    setGenerating(true);
    try {
      const r = await api.post("/shifts/generate", { restaurant_id: rid, week_start: fromISO, force });
      const gaps = r.data.gaps?.length || 0;
      toast[gaps ? "warning" : "success"](`${r.data.created} shifts gepland${r.data.skipped_days?.length ? ` · ${r.data.skipped_days.length} handmatige dag(en) behouden` : ""}${gaps ? ` · ${gaps} tekort(en)` : ""}`, {
        description: gaps ? r.data.gaps.slice(0, 3).join(" · ") : "Regels: max 12u/shift, 11u rust, max-uren per week, ≥1 vrije dag",
      });
      loadWeek();
    } catch (e) { toast.error(e?.response?.data?.detail || "Genereren mislukt"); }
    setGenerating(false);
  };

  const saveShift = async (form) => {
    try {
      const body = { restaurant_id: rid, ...form };
      if (form.id) await api.put(`/shifts/${form.id}`, body);
      else await api.post("/shifts", body);
      setEditing(null);
      toast.success("Opgeslagen");
      loadWeek();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    }
  };

  const removeShift = async (s) => {
    if (!confirm("Shift verwijderen?")) return;
    await api.delete(`/shifts/${s.id}`);
    loadWeek();
  };

  const removeExternal = async (emp) => {
    if (!confirm(`Personeelslid '${emp.name}' verwijderen?`)) return;
    try {
      await api.delete(`/shifts/external_employees/${emp.employee_id}`);
      toast.success("Verwijderd");
      loadWeek();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const addExternal = async (form) => {
    try {
      await api.post("/shifts/external_employees", { restaurant_id: rid, ...form });
      setAddingExternal(false);
      toast.success("Personeelslid toegevoegd");
      loadWeek();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toevoegen mislukt");
    }
  };

  const isPlatformOnly = ["platform_owner", "super_admin", "support", "cybersecurity"].includes(user?.role);
  const isAdmin = isPlatformOnly || ["admin", "organization_admin"].includes(user?.role);
  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-zinc-500">
        <Clock className="mx-auto mb-3 text-zinc-300" size={28} />
        Shiftregistratie is alleen toegankelijk voor beheerders.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="shift-registratie-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Personeel</div>
          <h1 className="text-2xl font-bold text-[#1B2D5C] flex items-center gap-2"><Clock size={22} /> Shiftregistratie</h1>
          <p className="text-xs text-zinc-500 mt-1">Uren per personeelslid met controle op de Nederlandse Arbeidstijdenwet (horeca).</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={rid} onChange={e => setRid(e.target.value)}
            className="h-9 border border-zinc-200 rounded-full px-3 text-sm bg-white" data-testid="shift-resto-select">
            {restaurants.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <button onClick={() => setWeekStartDate(w => addDays(w, -7))}
            className="w-9 h-9 border border-zinc-200 hover:border-[#1B2D5C] rounded-md inline-flex items-center justify-center"
            data-testid="shift-prev-week"><ChevronLeft size={16} /></button>
          <div className="text-sm font-semibold text-[#1B2D5C] min-w-[140px] text-center">
            Week {isoWeekNr(days[0])} · {days[0].toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}
          </div>
          <button onClick={() => setWeekStartDate(w => addDays(w, 7))}
            className="w-9 h-9 border border-zinc-200 hover:border-[#1B2D5C] rounded-md inline-flex items-center justify-center"
            data-testid="shift-next-week"><ChevronRight size={16} /></button>
          <button onClick={runAutoFill} disabled={autoFilling}
            className="inline-flex items-center gap-1.5 h-9 px-3 border border-[#5BC044]/40 hover:border-[#5BC044] text-[#5BC044] rounded-full text-sm font-semibold disabled:opacity-50"
            data-testid="shift-auto-fill">
            <RefreshCw size={13} className={autoFilling ? "animate-spin" : ""} /> Nu bijwerken
          </button>
          <button onClick={() => generateRoster(false)} disabled={generating || !rid}
            className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#101828] hover:bg-[#1B2D5C] text-white rounded-full text-sm font-semibold disabled:opacity-50"
            data-testid="shift-generate">
            <CalendarDays size={13} /> {generating ? "Plannen…" : "Rooster genereren"}
          </button>
          <button onClick={() => generateRoster(true)} disabled={generating || !rid} title="Automatische shifts van deze week opnieuw plannen"
            className="inline-flex items-center h-9 px-3 border border-zinc-200 hover:border-[#101828] text-zinc-600 rounded-full text-xs font-semibold disabled:opacity-50"
            data-testid="shift-generate-force">Opnieuw</button>
          <button onClick={() => setSettingsOpen(true)} disabled={!rid}
            className="inline-flex items-center gap-1.5 h-9 px-3 border border-zinc-200 hover:border-[#101828] text-zinc-700 rounded-full text-sm font-semibold disabled:opacity-50"
            data-testid="shift-roster-settings">
            <Clock size={13} /> Openingstijden
          </button>
        </div>
      </div>

      {/* Employee panel */}
      <div className="card-flat p-4 mb-4" data-testid="employee-settings">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#1B2D5C]">
            <Users size={15} /> Personeel
          </div>
          <button onClick={() => setAddingExternal(true)}
            className="inline-flex items-center gap-1.5 h-8 px-3 border border-zinc-300 hover:border-[#5BC044] text-[#5BC044] rounded text-xs font-semibold"
            data-testid="add-external-employee">
            <Plus size={13} /> Personeelslid toevoegen
          </button>
        </div>
        {employees.length === 0 ? (
          <div className="text-sm text-zinc-500 py-4 text-center">Voeg personeel toe om hun uren te registreren.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {employees.map(e => (
              <div key={e.employee_id} className="border border-zinc-200 rounded-md px-3 py-2 bg-white">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{e.name}</div>
                    <div className="text-[10px] text-zinc-500 uppercase">
                      {e.external ? "Zonder account" : `Login: ${e.role}`}
                    </div>
                  </div>
                  {e.external && (
                    <button onClick={() => removeExternal(e)}
                      className="text-red-500 hover:text-red-700 p-1"
                      data-testid={`remove-external-${e.employee_id}`}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
                <select value={e.function || ""} onChange={ev => setFunction(e, ev.target.value)}
                  className="w-full h-7 border border-zinc-200 rounded px-1 text-xs bg-white"
                  data-testid={`emp-function-${e.employee_id}`}>
                  <option value="">— Functie kiezen —</option>
                  {FUNCTION_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
                <label className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-zinc-500">
                  <span>Max uren/week</span>
                  <input type="number" min="4" max="60" defaultValue={e.max_hours_week ?? 40}
                    onBlur={ev => Number(ev.target.value) !== (e.max_hours_week ?? 40) && setMaxHours(e, ev.target.value)}
                    className="w-16 h-7 border border-zinc-200 rounded px-1 text-xs text-right font-mono bg-white"
                    data-testid={`emp-max-hours-${e.employee_id}`} />
                </label>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Violations banner */}
      {violations.length > 0 && (
        <div className="card-flat p-3 mb-4 border-l-4 border-l-amber-500 bg-amber-50/60" data-testid="violation-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-amber-800">Overtreding(en) van de Arbeidstijdenwet:</div>
              <ul className="text-xs text-amber-700 mt-1 space-y-0.5 list-disc list-inside">
                {violations.slice(0, 5).map((v, i) => <li key={i}>{v.employee_name}: {v.message}</li>)}
                {violations.length > 5 && <li className="italic">…en nog {violations.length - 5} andere.</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Week grid */}
      <div className="card-flat overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#1A2B5E] text-white">
              <th className="text-left px-3 py-2 w-44">Medewerker</th>
              {days.map((d, i) => (
                <th key={i} className="text-center px-2 py-2">
                  <div className="font-semibold">{DAYS_NL[i].slice(0, 3)}</div>
                  <div className="text-[10px] opacity-80">{d.getDate()}/{d.getMonth() + 1}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-zinc-500">Voeg eerst personeel toe aan dit restaurant (Instellingen · Team).</td></tr>
            ) : employees.map(emp => (
              <tr key={emp.employee_id} className="border-t border-zinc-100">
                <td className="px-3 py-2">
                  <div className="font-medium">{emp.name}</div>
                  {emp.function && (
                    <div className="text-[10px] text-zinc-500">{FUNCTION_OPTIONS.find(o => o.v === emp.function)?.l || emp.function}</div>
                  )}
                </td>
                {days.map((d, i) => {
                  const dayISO = iso(d);
                  const s = cellShifts(emp.employee_id, dayISO);
                  const dv = dailyViolations(emp.employee_id, dayISO);
                  return (
                    <td key={i} className="px-1 py-1 align-top" data-testid={`cell-${emp.employee_id}-${dayISO}`}>
                      {s.map(sh => {
                        const vs = violationsFor(sh.id);
                        return (
                          <button key={sh.id} onClick={() => setEditing(sh)}
                            className={`w-full text-left mb-1 rounded-md px-2 py-1 border ${vs.length ? "border-amber-400 bg-amber-50" : (sh.auto_filled ? "border-blue-200 bg-blue-50" : "border-emerald-200 bg-emerald-50/40")} hover:border-[#1B2D5C]`}
                            data-testid={`shift-${sh.id}`}>
                            <div className="font-mono text-[11px]">{sh.start_time}–{sh.end_time}</div>
                            {sh.break_min > 0 && <div className="text-[9px] text-zinc-500">{sh.break_min}m pauze</div>}
                            {vs.length > 0 && <div className="text-[9px] text-amber-700 flex items-center gap-1"><AlertTriangle size={9} /> {vs.length} overtreding</div>}
                          </button>
                        );
                      })}
                      <button onClick={() => setEditing({ employee_id: emp.employee_id, employee_name: emp.name, date: dayISO, start_time: "10:00", end_time: "18:00", break_min: 30 })}
                        className="w-full text-[10px] text-zinc-400 hover:text-[#5BC044] py-1"
                        data-testid={`add-${emp.employee_id}-${dayISO}`}>
                        <Plus size={11} className="inline" /> shift
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {settingsOpen && <RosterSettingsModal rid={rid} onClose={() => setSettingsOpen(false)} />}
      {editing && (
        <ShiftEditor initial={editing} onCancel={() => setEditing(null)} onSave={saveShift} onDelete={removeShift} />
      )}
      {addingExternal && (
        <ExternalEmployeeAdder onCancel={() => setAddingExternal(false)} onSave={addExternal} />
      )}
    </div>
  );
}

function ExternalEmployeeAdder({ onCancel, onSave }) {
  const [name, setName] = useState("");
  const [fn, setFn] = useState("");
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onCancel} data-testid="add-external-modal">
      <div className="bg-white rounded-lg w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
          <div>
            <div className="font-bold text-[#1B2D5C]">Personeelslid toevoegen</div>
            <div className="text-xs text-zinc-500">Zonder Stockly-account (freelancer, uitzendkracht, oproepkracht).</div>
          </div>
          <button onClick={onCancel} className="w-8 h-8 rounded hover:bg-zinc-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-[#1B2D5C] mb-1">Naam</label>
            <input value={name} onChange={e => setName(e.target.value)}
              placeholder="Voor- en achternaam"
              className="w-full h-9 border border-zinc-200 rounded px-2 text-sm"
              data-testid="external-name" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#1B2D5C] mb-1">Functie</label>
            <select value={fn} onChange={e => setFn(e.target.value)}
              className="w-full h-9 border border-zinc-200 rounded px-2 text-sm bg-white"
              data-testid="external-function">
              <option value="">— Kies functie —</option>
              {FUNCTION_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-100">
          <button onClick={onCancel} className="h-9 px-3 border border-zinc-300 rounded text-sm">Annuleren</button>
          <button onClick={() => name.trim() && onSave({ name: name.trim(), function: fn })}
            disabled={!name.trim()}
            className="h-9 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded text-sm font-semibold disabled:opacity-50"
            data-testid="external-save">
            Toevoegen
          </button>
        </div>
      </div>
    </div>
  );
}

function ShiftEditor({ initial, onCancel, onSave, onDelete }) {
  const [f, setF] = useState({
    id: initial.id, employee_id: initial.employee_id, employee_name: initial.employee_name || "",
    date: initial.date, start_time: initial.start_time, end_time: initial.end_time,
    break_min: initial.break_min ?? 30, notes: initial.notes || "",
  });
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onCancel} data-testid="shift-editor-modal">
      <div className="bg-white rounded-lg w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
          <div>
            <div className="font-bold text-[#1B2D5C]">{f.id ? "Shift bewerken" : "Shift toevoegen"}</div>
            <div className="text-xs text-zinc-500">{f.employee_name} · {new Date(f.date).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "short" })}</div>
          </div>
          <button onClick={onCancel} className="w-8 h-8 rounded hover:bg-zinc-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#1B2D5C] mb-1">Starttijd</label>
              <input type="time" value={f.start_time} onChange={e => setF({ ...f, start_time: e.target.value })}
                className="w-full h-9 border border-zinc-200 rounded px-2 text-sm" data-testid="shift-start" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#1B2D5C] mb-1">Eindtijd</label>
              <input type="time" value={f.end_time} onChange={e => setF({ ...f, end_time: e.target.value })}
                className="w-full h-9 border border-zinc-200 rounded px-2 text-sm" data-testid="shift-end" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#1B2D5C] mb-1">Pauze (min)</label>
            <input type="number" min="0" step="15" value={f.break_min} onChange={e => setF({ ...f, break_min: parseInt(e.target.value) || 0 })}
              className="w-full h-9 border border-zinc-200 rounded px-2 text-sm" data-testid="shift-break" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#1B2D5C] mb-1">Notitie</label>
            <input value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })}
              className="w-full h-9 border border-zinc-200 rounded px-2 text-sm" placeholder="Optioneel…" />
          </div>
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-100">
          {f.id ? (
            <button onClick={() => onDelete(f)} className="text-sm text-red-600 hover:text-red-800 inline-flex items-center gap-1" data-testid="shift-delete">
              <Trash2 size={13} /> Verwijderen
            </button>
          ) : <div />}
          <div className="flex items-center gap-2">
            <button onClick={onCancel} className="h-9 px-3 border border-zinc-300 rounded text-sm">Annuleren</button>
            <button onClick={() => onSave(f)} className="h-9 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded text-sm font-semibold" data-testid="shift-save">Opslaan</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const DAY_LABELS = { mon: "Ma", tue: "Di", wed: "Wo", thu: "Do", fri: "Vr", sat: "Za", sun: "Zo" };
function RosterSettingsModal({ rid, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/restaurants/${rid}/roster_settings`).then(r => setCfg(r.data)).catch(() => toast.error("Laden mislukt")); }, [rid]);
  const setDay = (d, patch) => setCfg(c => ({ ...c, opening_hours: { ...c.opening_hours, [d]: { ...c.opening_hours[d], ...patch } } }));
  const toggleBusy = (d) => setCfg(c => ({ ...c, busy_days: c.busy_days.includes(d) ? c.busy_days.filter(x => x !== d) : [...c.busy_days, d] }));
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/restaurants/${rid}/roster_settings`, { opening_hours: cfg.opening_hours, busy_days: cfg.busy_days });
      toast.success("Openingstijden opgeslagen — het rooster volgt deze tijden");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="roster-settings-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="font-bold text-[#101828]">Openingstijden & drukke dagen</div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center text-zinc-500" data-testid="roster-settings-close"><X size={18} /></button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Het auto-rooster start 1 uur vóór opening en eindigt 30 min na sluiting. Drukke dagen krijgen 3 in de keuken en 3 in de bediening; anders minimaal 1 + 1 bij opening en sluiting.</p>
        {!cfg ? <div className="text-sm text-zinc-500">Laden…</div> : (
          <div className="space-y-1.5">
            {Object.keys(DAY_LABELS).map(d => {
              const v = cfg.opening_hours[d];
              return (
                <div key={d} className={`grid grid-cols-[36px_1fr_1fr_auto_auto] items-center gap-2 text-sm rounded-xl px-2 py-1 ${v.closed ? "opacity-50" : ""}`} data-testid={`roster-day-${d}`}>
                  <span className="font-semibold">{DAY_LABELS[d]}</span>
                  <input type="time" value={v.open} disabled={v.closed} onChange={e => setDay(d, { open: e.target.value })} className="h-8 border border-zinc-200 rounded px-1 text-xs font-mono" data-testid={`roster-open-${d}`} />
                  <input type="time" value={v.close} disabled={v.closed} onChange={e => setDay(d, { close: e.target.value })} className="h-8 border border-zinc-200 rounded px-1 text-xs font-mono" data-testid={`roster-close-${d}`} />
                  <label className="text-[11px] inline-flex items-center gap-1"><input type="checkbox" checked={v.closed} onChange={e => setDay(d, { closed: e.target.checked })} /> dicht</label>
                  <label className="text-[11px] inline-flex items-center gap-1"><input type="checkbox" checked={cfg.busy_days.includes(d)} onChange={() => toggleBusy(d)} data-testid={`roster-busy-${d}`} /> druk</label>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="h-9 px-4 rounded-full border border-zinc-200 text-sm font-semibold">Annuleren</button>
          <button onClick={save} disabled={saving || !cfg} className="h-9 px-4 rounded-full bg-[#5BC044] hover:bg-[#4FAA3A] text-white text-sm font-semibold disabled:opacity-50" data-testid="roster-settings-save">Opslaan</button>
        </div>
      </div>
    </div>
  );
}
